import { useState, useRef } from "react";
import { Filter, Plus, X, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PortalDropdown } from "./PortalDropdown";

const OPERATORS = {
  text: [
    { key: "contains", label: "contient" },
    { key: "not_contains", label: "ne contient pas" },
    { key: "equals", label: "est egal a" },
    { key: "not_equals", label: "n'est pas egal a" },
    { key: "starts_with", label: "commence par" },
    { key: "is_empty", label: "est vide" },
    { key: "is_not_empty", label: "n'est pas vide" },
  ],
  currency: [
    { key: "equals", label: "=" },
    { key: "not_equals", label: "!=" },
    { key: "gt", label: ">" },
    { key: "gte", label: ">=" },
    { key: "lt", label: "<" },
    { key: "lte", label: "<=" },
    { key: "is_empty", label: "est vide" },
  ],
  number: [
    { key: "equals", label: "=" },
    { key: "not_equals", label: "!=" },
    { key: "gt", label: ">" },
    { key: "gte", label: ">=" },
    { key: "lt", label: "<" },
    { key: "lte", label: "<=" },
  ],
  date: [
    { key: "equals", label: "est" },
    { key: "before", label: "avant" },
    { key: "after", label: "apres" },
    { key: "is_empty", label: "est vide" },
    { key: "is_not_empty", label: "n'est pas vide" },
  ],
  select: [
    { key: "equals", label: "est" },
    { key: "not_equals", label: "n'est pas" },
  ],
  checkbox: [
    { key: "is_true", label: "est coche" },
    { key: "is_false", label: "n'est pas coche" },
  ],
};

function matchFilter(item, filter, config) {
  const val = item[filter.field];
  const fv = filter.value;
  const op = filter.operator;

  if (op === "is_empty") return !val && val !== 0;
  if (op === "is_not_empty") return !!val || val === 0;
  if (op === "is_true") return !!val;
  if (op === "is_false") return !val;

  const strVal = String(val || "").toLowerCase();
  const strFv = String(fv || "").toLowerCase();

  switch (op) {
    case "contains": return strVal.includes(strFv);
    case "not_contains": return !strVal.includes(strFv);
    case "equals": {
      if (typeof val === "number") return val === Number(fv);
      return strVal === strFv;
    }
    case "not_equals": {
      if (typeof val === "number") return val !== Number(fv);
      return strVal !== strFv;
    }
    case "starts_with": return strVal.startsWith(strFv);
    case "gt": return Number(val || 0) > Number(fv || 0);
    case "gte": return Number(val || 0) >= Number(fv || 0);
    case "lt": return Number(val || 0) < Number(fv || 0);
    case "lte": return Number(val || 0) <= Number(fv || 0);
    case "before": return strVal < strFv;
    case "after": return strVal > strFv;
    default: return true;
  }
}

export function applyAdvancedFilters(items, filters, logic, config) {
  if (!filters.length) return items;
  return items.filter(item => {
    const results = filters.map(f => matchFilter(item, f, config));
    return logic === "OR" ? results.some(Boolean) : results.every(Boolean);
  });
}

export default function AdvancedFilterBar({ columns, config, filters, setFilters, logic, setLogic }) {
  const [showAdd, setShowAdd] = useState(false);
  const [fieldKey, setFieldKey] = useState("");
  const [operator, setOperator] = useState("");
  const [value, setValue] = useState("");
  const triggerRef = useRef(null);

  const filterableCols = columns.filter(c => c.visible);

  const selectedCol = filterableCols.find(c => c.key === fieldKey);
  const colType = selectedCol?.type || "text";
  const ops = OPERATORS[colType] || OPERATORS.text;

  const addFilter = () => {
    if (!fieldKey || !operator) return;
    const needsValue = !["is_empty", "is_not_empty", "is_true", "is_false"].includes(operator);
    if (needsValue && !value) return;
    setFilters([...filters, { field: fieldKey, operator, value, label: selectedCol?.label || fieldKey }]);
    setFieldKey(""); setOperator(""); setValue(""); setShowAdd(false);
  };

  const removeFilter = (i) => setFilters(filters.filter((_, idx) => idx !== i));
  const clearAll = () => setFilters([]);

  const getOpLabel = (op, type) => (OPERATORS[type] || OPERATORS.text).find(o => o.key === op)?.label || op;

  const getSelectOptions = (col) => {
    if (!col?.source || !config) return [];
    return config[col.source] || [];
  };

  if (!filters.length && !showAdd) {
    return (
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs hover:bg-white/5 transition-colors border border-white/10" style={{ color: "var(--text-muted)" }} data-testid="add-filter-btn">
          <Filter className="w-3.5 h-3.5" /> Filtrer
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="advanced-filter-bar">
      <Filter className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--text-muted)" }} />

      {filters.length > 1 && (
        <button onClick={() => setLogic(logic === "AND" ? "OR" : "AND")} className="px-2 py-1 rounded text-[10px] font-bold border border-white/10 hover:border-[#f7c935]/30 transition-colors" style={{ color: "#f7c935" }} data-testid="logic-toggle">
          {logic}
        </button>
      )}

      {filters.map((f, i) => {
        const col = filterableCols.find(c => c.key === f.field);
        const displayVal = f.value;
        const selectOpts = getSelectOptions(col);
        const selectLabel = selectOpts.find(o => o.key === displayVal)?.label;

        return (
          <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] border border-white/10 bg-[#f7c935]/5" data-testid={`filter-chip-${i}`}>
            <span className="font-bold" style={{ color: "#f7c935" }}>{f.label}</span>
            <span style={{ color: "var(--text-muted)" }}>{getOpLabel(f.operator, col?.type || "text")}</span>
            {f.value && <span className="font-bold" style={{ color: "var(--text-primary)" }}>{selectLabel || f.value}</span>}
            <button onClick={() => removeFilter(i)} className="ml-1 p-0.5 rounded hover:bg-red-500/10"><X className="w-3 h-3 text-red-400" /></button>
          </div>
        );
      })}

      <div className="relative">
        <button ref={triggerRef} onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] border border-dashed border-white/10 hover:border-[#f7c935]/30 transition-colors" style={{ color: "var(--text-muted)" }} data-testid="add-filter-chip-btn">
          <Plus className="w-3 h-3" /> Ajouter un filtre
        </button>

        <PortalDropdown open={showAdd} onClose={() => setShowAdd(false)} triggerRef={triggerRef} align="left" width={288}>
          <div className="p-3 space-y-3" data-testid="filter-dropdown">
            <div>
              <label className="text-[10px] font-bold block mb-1" style={{ color: "var(--text-muted)" }}>Champ</label>
              <Select value={fieldKey} onValueChange={(v) => { setFieldKey(v); setOperator(""); setValue(""); }}>
                <SelectTrigger className="bg-[#0b0811] border-white/10 text-white text-xs h-8" data-testid="filter-field-select">
                  <SelectValue placeholder="Selectionner..." />
                </SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-72">
                  {filterableCols.map(c => (
                    <SelectItem key={c.key} value={c.key} className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid={`filter-field-option-${c.key}`}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {fieldKey && (
              <div>
                <label className="text-[10px] font-bold block mb-1" style={{ color: "var(--text-muted)" }}>Condition</label>
                <Select value={operator} onValueChange={setOperator}>
                  <SelectTrigger className="bg-[#0b0811] border-white/10 text-white text-xs h-8" data-testid="filter-operator-select">
                    <SelectValue placeholder="Selectionner..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-72">
                    {ops.map(o => (
                      <SelectItem key={o.key} value={o.key} className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid={`filter-operator-option-${o.key}`}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {operator && !["is_empty", "is_not_empty", "is_true", "is_false"].includes(operator) && (
              <div>
                <label className="text-[10px] font-bold block mb-1" style={{ color: "var(--text-muted)" }}>Valeur</label>
                {colType === "select" && selectedCol?.source ? (
                  <Select value={value} onValueChange={setValue}>
                    <SelectTrigger className="bg-[#0b0811] border-white/10 text-white text-xs h-8" data-testid="filter-value-select">
                      <SelectValue placeholder="Selectionner..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-72">
                      {(config[selectedCol.source] || []).map(o => (
                        <SelectItem key={o.key} value={o.key} className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid={`filter-value-option-${o.key}`}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : colType === "date" ? (
                  <Input type="date" value={value} onChange={e => setValue(e.target.value)} className="bg-[#0b0811] border-white/10 text-white text-xs h-8" data-testid="filter-value-input" />
                ) : (
                  <Input value={value} onChange={e => setValue(e.target.value)} onKeyDown={e => e.key === "Enter" && addFilter()} placeholder="Valeur..." className="bg-[#0b0811] border-white/10 text-white text-xs h-8" data-testid="filter-value-input" />
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button onClick={() => setShowAdd(false)} variant="outline" className="border-white/10 text-slate-400 text-xs h-7 px-2">Annuler</Button>
              <Button onClick={addFilter} disabled={!fieldKey || !operator} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs h-7 px-3" data-testid="apply-filter-btn">Appliquer</Button>
            </div>
          </div>
        </PortalDropdown>
      </div>

      {filters.length > 0 && (
        <button onClick={clearAll} className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] hover:bg-red-500/10 transition-colors text-red-400" data-testid="clear-filters-btn">
          <Trash2 className="w-3 h-3" /> Tout effacer
        </button>
      )}
    </div>
  );
}
